import { Checkbox } from 'primereact/checkbox';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useCallback, useContext, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { usePhoneOptionsContext } from '../../../../../context/PhoneOptionsContext';
import {
  ShippingLocation,
  UnregisteredClientId,
} from '../../../../../types/api/orders';
import { LabelValue } from '../../../../../types/options';
import { ReduxState } from '../../../../../types/redux';
import { isStreetFreeInputAllowed } from '../../../../../utils/constants/misc';
import { invalidPhoneNumberCharactersRegex } from '../../../../../utils/constants/phoneNumbers';
import { placeItemTemplate } from '../../../../../utils/helpers/misc';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
} from '../../../../../utils/helpers/phoneNumbers';
import AutoCompleteInput from '../../../../Forms/AutoCompleteInput/AutoCompleteInput';
import FieldWithErrors from '../../../../Forms/ReactHookForm/FieldWithErrors/FieldWithErrors';
import Step from '../../../../Stepper/Inline/Step';
import { CustomerRole, FormFields } from '../CreateEditRecreate.functions';
import StepsContext from '../StepsContext';
import Contact from './Fields/Contact';
import SaveContact from './Fields/SaveContact';

function Recipient(): JSX.Element {
  const { t } = useTranslation();

  const {
    recipient: {
      isPlacesDataLoading,
      placesOptions,
      isStreetsDataLoading,
      streetOptions,
      subsidiaries,
      hubOptions,
      isContactDataLoading,
      contactData,
      reloadContactData,
    },
  } = useContext(StepsContext);

  const userClientId = useSelector<ReduxState, ReduxState['user']['client_id']>(
    (state) => state?.user.client_id
  );

  const { setValue } = useFormContext<FormFields>();

  const internal_shipment = useWatch<FormFields, 'internal_shipment'>({
    name: 'internal_shipment',
  });

  const customer_role = useWatch<FormFields, 'customer_role'>({
    name: 'customer_role',
  });

  const mesto_do_id = useWatch<FormFields, 'mesto_do_id'>({
    name: 'mesto_do_id',
  });

  const mesto_do_ime = useWatch<FormFields, 'mesto_do_ime'>({
    name: 'mesto_do_ime',
  });

  const ulica_do_id = useWatch<FormFields, 'ulica_do_id'>({
    name: 'ulica_do_id',
  });

  const ulica_do_ime = useWatch<FormFields, 'ulica_do_ime'>({
    name: 'ulica_do_ime',
  });

  const klient_od_id = useWatch<FormFields, 'klient_od_id'>({
    name: 'klient_od_id',
  });

  const mobilen_do_region = useWatch<FormFields, 'mobilen_do_region'>({
    name: 'mobilen_do_region',
  });

  const telefon_do_region = useWatch<FormFields, 'telefon_do_region'>({
    name: 'telefon_do_region',
  });

  const delivery_location_type_id = useWatch<
    FormFields,
    'delivery_location_type_id'
  >({
    name: 'delivery_location_type_id',
  });

  const handlePlaceFilterChange = useCallback(
    (value: string) => {
      setValue('mesto_do_ime', value);
    },
    [setValue]
  );

  const handlePlaceSelectionChange = useCallback(
    (value: string | null) => {
      setValue('mesto_do_id', value ?? '');
    },
    [setValue]
  );

  const handleStreetFilterChange = useCallback(
    (value: string) => {
      setValue('ulica_do_ime', value);
    },
    [setValue]
  );

  const handleStreetSelectionChange = useCallback(
    (value: string | null) => {
      setValue('ulica_do_id', value ?? '');
    },
    [setValue]
  );

  const subsidiaryOptions: LabelValue<number>[] =
    subsidiaries
      ?.filter((s) => s.id !== klient_od_id)
      .map((subsidiary) => {
        return { label: subsidiary.ime, value: subsidiary.id };
      }) ?? [];

  function handleRecipientSubsidiaryChange(e: DropdownChangeParams) {
    const subsidiary = subsidiaries?.find((s) => {
      return s.id === e.value;
    });

    setValue('klient_do_id', subsidiary?.id ?? UnregisteredClientId.Recipient);
    setValue('klient_do_ime', subsidiary?.ime ?? '');
    setValue('mesto_do_id', subsidiary?.address.placeId ?? '');
    setValue('mesto_do_ime', subsidiary?.address.placeName ?? '');
    setValue('mobilen_do', subsidiary?.mobilen ?? '');
    setValue('telefon_do', subsidiary?.telefon ?? '');
    setValue('recipientInvoice', subsidiary?.faktura ?? 0);
    if (isStreetFreeInputAllowed) {
      setValue('adresa_do', subsidiary?.address.rawName ?? '');
    } else {
      setValue('ulica_do_id', subsidiary?.address.streetId ?? '');
      setValue('ulica_do_ime', subsidiary?.address.streetName ?? '');
      setValue('broj_do', subsidiary?.address.housenumber ?? '');
      setValue('vlez_do', subsidiary?.address.entrance ?? '');
      setValue('stan_do', subsidiary?.address.apartment ?? '');
    }
  }

  const isHeadquarter = useMemo<boolean>(() => {
    const me = subsidiaries?.find((s) => s.id === userClientId);

    return !!me && me?.id === me?.direkcija_id;
  }, [subsidiaries, userClientId]);

  const isDropdownShown =
    internal_shipment ||
    (isHeadquarter && customer_role === CustomerRole.Recipient);

  const { countryOptionTemplate, languages, selectedCountryTemplate } =
    usePhoneOptionsContext();

  return (
    <Step title={t('Recipient')} className="p-fluid">
      {!isDropdownShown ? (
        <FieldWithErrors name="klient_do_ime" label={t('Recipient name')}>
          <Contact
            isOptionsLoading={isContactDataLoading}
            contactData={contactData}
            reloadContactData={reloadContactData}
            role={CustomerRole.Recipient}
          />
        </FieldWithErrors>
      ) : (
        <FieldWithErrors name="klient_do_id" label={t('Recipient name')}>
          <Controller
            name="klient_do_id"
            render={({ field }) => (
              <Dropdown
                name="klient_do_id"
                inputId="klient_do_id"
                value={field.value}
                filter
                options={subsidiaryOptions}
                onChange={handleRecipientSubsidiaryChange}
                className="data-cy-recipient"
              />
            )}
          />
        </FieldWithErrors>
      )}

      <div className="p-grid">
        <FieldWithErrors
          className="p-col-12 p-md-6"
          name="mobilen_do"
          label={t('Mobile')}
        >
          <div className="p-inputgroup">
            <Controller
              name="mobilen_do_region"
              render={({ field }) => (
                <Dropdown
                  style={{ padding: 0, width: '5rem' }}
                  id="mobilen_do_region"
                  options={languages}
                  itemTemplate={countryOptionTemplate}
                  valueTemplate={selectedCountryTemplate}
                  data-cy="mobilen_do_region"
                  {...field}
                />
              )}
            />
            <Controller
              name="mobilen_do"
              render={({ field }) => (
                <InputText
                  name="mobilen_do"
                  style={{ width: '100%' }}
                  value={formatMobileNumber(
                    field.value,
                    mobilen_do_region ||
                      getPhoneorMobileNumberRegion(field.value)
                  )}
                  onChange={(e) => {
                    field.onChange(
                      e.target.value?.replace(
                        invalidPhoneNumberCharactersRegex,
                        ''
                      )
                    );
                  }}
                  className="data-cy-mobile"
                />
              )}
            />
          </div>
        </FieldWithErrors>

        <FieldWithErrors
          className="p-col-12 p-md-6"
          name="telefon_do"
          label={t('Phone')}
        >
          <div className="p-inputgroup">
            <Controller
              name="telefon_do_region"
              render={({ field }) => (
                <Dropdown
                  style={{ padding: 0, width: '5rem' }}
                  id="telefon_do_region"
                  options={languages}
                  itemTemplate={countryOptionTemplate}
                  valueTemplate={selectedCountryTemplate}
                  data-cy="telefon_do_region"
                  {...field}
                />
              )}
            />
            <Controller
              name="telefon_do"
              render={({ field }) => (
                <InputText
                  name="telefon_do"
                  style={{ width: '100%' }}
                  value={formatPhoneNumber(
                    field.value,
                    telefon_do_region ||
                      getPhoneorMobileNumberRegion(field.value)
                  )}
                  onChange={(e) => {
                    field.onChange(
                      e.target.value?.replace(
                        invalidPhoneNumberCharactersRegex,
                        ''
                      )
                    );
                  }}
                  className="data-cy-phone"
                />
              )}
            />
          </div>
        </FieldWithErrors>
      </div>

      <FieldWithErrors name="mesto_do_id" label={t('Place')}>
        <Controller
          name="mesto_do_id"
          render={({ field }) => (
            <AutoCompleteInput
              filterValue={mesto_do_ime}
              value={field.value}
              onFilterChange={handlePlaceFilterChange}
              onSelectionChange={handlePlaceSelectionChange}
              options={placesOptions}
              itemTemplate={placeItemTemplate}
              valueTemplate={placeItemTemplate}
              placeholder={isPlacesDataLoading ? t('Loading...') : ''}
              filterDataCy="place"
              optionsClassName="data-cy-place-options"
            />
          )}
        />
      </FieldWithErrors>

      {isStreetFreeInputAllowed ? (
        <FieldWithErrors name="adresa_do" label={t('Address')}>
          <Controller
            name="adresa_do"
            render={({ field }) => (
              <InputText {...field} disabled={!mesto_do_id} maxLength={256} />
            )}
          />
        </FieldWithErrors>
      ) : (
        <>
          <FieldWithErrors name="ulica_do_id" label={t('Street')}>
            <Controller
              name="ulica_do_id"
              render={({ field }) => (
                <AutoCompleteInput
                  lazy={false}
                  name="ulica_do_id"
                  id="ulica_do_id"
                  filterValue={ulica_do_ime ?? ''}
                  value={field.value}
                  onFilterChange={handleStreetFilterChange}
                  onSelectionChange={handleStreetSelectionChange}
                  options={streetOptions}
                  disabled={!mesto_do_id || isStreetsDataLoading}
                  placeholder={isStreetsDataLoading ? 'Loading...' : ''}
                  filterDataCy="street"
                  optionsClassName="data-cy-street"
                />
              )}
            />
          </FieldWithErrors>

          <div className="p-grid">
            <div className="p-col-4">
              <FieldWithErrors name="broj_do" label={t('Street No.')}>
                <Controller
                  name="broj_do"
                  render={({ field }) => (
                    <InputText
                      id="broj_do"
                      name="broj_do"
                      value={field.value}
                      disabled={!ulica_do_id}
                      onChange={field.onChange}
                      className="data-cy-street_no"
                    />
                  )}
                />
              </FieldWithErrors>
            </div>

            <div className="p-col-4">
              <FieldWithErrors name="vlez_do" label={t('Entrance No.')}>
                <Controller
                  name="vlez_do"
                  render={({ field }) => (
                    <InputText
                      id="vlez_do"
                      name="vlez_do"
                      value={field.value}
                      disabled={!ulica_do_id}
                      onChange={field.onChange}
                    />
                  )}
                />
              </FieldWithErrors>
            </div>

            <div className="p-col-4">
              <FieldWithErrors name="stan_do" label={t('Flat No.')}>
                <Controller
                  name="stan_do"
                  render={({ field }) => (
                    <InputText
                      id="stan_do"
                      name="stan_do"
                      value={field.value}
                      disabled={!ulica_do_id}
                      onChange={field.onChange}
                    />
                  )}
                />
              </FieldWithErrors>
            </div>
          </div>
        </>
      )}

      <FieldWithErrors
        name="delivery_location_type_id"
        label={t('Recipient picks up from warehouse')}
      >
        <div className="p-d-flex p-jc-around p-ai-center">
          <div className="p-col-1">
            <Controller
              name="delivery_location_type_id"
              render={({ field }) => (
                <Checkbox
                  inputId="delivery_location_type_id"
                  name="delivery_location_type_id"
                  value={field.value}
                  checked={field.value === ShippingLocation.Hub}
                  onChange={(e) => {
                    field.onChange(
                      e.checked
                        ? ShippingLocation.Hub
                        : ShippingLocation.HomeAddress
                    );
                  }}
                  className="data-cy-ship_from_hub"
                />
              )}
            />
          </div>

          <div className="p-col">
            <Controller
              name="hub_do_id"
              render={({ field }) => (
                <Dropdown
                  inputId="hub_do_id"
                  name="hub_do_id"
                  value={String(field.value)}
                  options={hubOptions}
                  filter
                  disabled={delivery_location_type_id !== ShippingLocation.Hub}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </div>
      </FieldWithErrors>

      {!isDropdownShown && <SaveContact isSender={false} />}
    </Step>
  );
}

export default Recipient;
